import React, { useRef } from 'react';

export interface RecipeFormValues {
  title: string;
  description: string;
  prepTime: string;
  totalTime: string;
  caloriesPerServing: string;
  servings: string;
}

interface Props {
  values: RecipeFormValues;
  errors: any;
  imageUrl: string;
  ingredients: string[];
  directions: string[];
  loading: boolean;
  submitLabel: string;
  onChange: (e: any) => void;
  onImageSelect: (e: any) => void;
  onSubmit: (e: any) => void;
  onBack: () => void;
  onIngredientsChange: (updated: string[]) => void;
  onDirectionsChange: (updated: string[]) => void;
  extraActions?: React.ReactNode;
}

const fieldLabelStyle: React.CSSProperties = {
  fontSize: '0.72rem',
  fontWeight: 600,
  color: '#6d7175',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  display: 'block',
  marginBottom: 4,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '7px 10px',
  border: '1px solid #e1e3e5',
  borderRadius: 6,
  fontSize: '0.85rem',
  color: '#202223',
  background: '#fff',
  outline: 'none',
};

const inputErrorStyle: React.CSSProperties = {
  ...inputStyle,
  border: '1px solid #ec174f',
};

const Field = ({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) => (
  <div>
    <label style={fieldLabelStyle}>{label}</label>
    {children}
    {error && <span style={{ fontSize: '0.75rem', color: '#ec174f', display: 'block', marginTop: 2 }}>{error}</span>}
  </div>
);

const RecipeForm = ({
  values,
  errors,
  imageUrl,
  ingredients,
  directions,
  loading,
  submitLabel,
  onChange,
  onImageSelect,
  onSubmit,
  onBack,
  onIngredientsChange,
  onDirectionsChange,
  extraActions,
}: Props) => {
  const imageInputRef = useRef<HTMLInputElement>(null);

  const handleIngredientChange = (index: number, value: string) => {
    const updated = [...ingredients];
    updated[index] = value;
    onIngredientsChange(updated);
  };

  const addIngredient = (index: number) => {
    if (ingredients[index] !== '') onIngredientsChange([...ingredients, '']);
  };

  const removeIngredient = (index: number) => {
    const updated = [...ingredients];
    updated.splice(index, 1);
    onIngredientsChange(updated);
  };

  const handleDirectionChange = (index: number, value: string) => {
    const updated = [...directions];
    updated[index] = value;
    onDirectionsChange(updated);
  };

  const addDirection = (index: number) => {
    if (directions[index] !== '') onDirectionsChange([...directions, '']);
  };

  const removeDirection = (index: number) => {
    const updated = [...directions];
    updated.splice(index, 1);
    onDirectionsChange(updated);
  };

  return (
    <div className="admin-card">
      <div className="admin-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <Field label="Title" error={errors.title}>
              <input
                style={errors.title ? inputErrorStyle : inputStyle}
                placeholder="Title"
                onChange={onChange}
                value={values.title}
                name="title"
              />
            </Field>
          </div>

          <div style={{ gridColumn: '1 / -1' }}>
            <Field label="Description" error={errors.description}>
              <textarea
                style={{ ...(errors.description ? inputErrorStyle : inputStyle), minHeight: 80, resize: 'vertical' }}
                placeholder="Description"
                onChange={onChange}
                value={values.description}
                name="description"
              />
            </Field>
          </div>

          <Field label="Preparation Time" error={errors.prepTime}>
            <input
              style={errors.prepTime ? inputErrorStyle : inputStyle}
              placeholder="e.g. 15 mins"
              onChange={onChange}
              value={values.prepTime}
              name="prepTime"
            />
          </Field>

          <Field label="Total Time" error={errors.totalTime}>
            <input
              style={errors.totalTime ? inputErrorStyle : inputStyle}
              placeholder="e.g. 30 mins"
              onChange={onChange}
              value={values.totalTime}
              name="totalTime"
            />
          </Field>

          <Field label="Calories per Serving" error={errors.caloriesPerServing}>
            <input
              style={errors.caloriesPerServing ? inputErrorStyle : inputStyle}
              type="number"
              placeholder="e.g. 250"
              onChange={onChange}
              value={values.caloriesPerServing}
              name="caloriesPerServing"
            />
          </Field>

          <Field label="Servings" error={errors.servings}>
            <input
              style={errors.servings ? inputErrorStyle : inputStyle}
              type="number"
              placeholder="e.g. 4"
              onChange={onChange}
              value={values.servings}
              name="servings"
            />
          </Field>
        </div>

        {/* Ingredients */}
        <div>
          <label style={fieldLabelStyle}>Ingredients</label>
          {errors.ingredients && <span style={{ fontSize: '0.75rem', color: '#ec174f', display: 'block', marginBottom: 4 }}>{errors.ingredients}</span>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {ingredients.map((ingredient, index) => (
              <div key={index} style={{ display: 'flex', gap: 6 }}>
                <input
                  style={{ ...inputStyle, flex: 1 }}
                  placeholder={`${index + 1}. ingredient`}
                  value={ingredient}
                  onChange={(e) => handleIngredientChange(index, e.target.value)}
                />
                {ingredients.length > 1 && (
                  <button
                    type="button"
                    className="admin-dropdown-trigger"
                    style={{ padding: '0 10px' }}
                    onClick={() => removeIngredient(index)}
                  >
                    −
                  </button>
                )}
                {index === ingredients.length - 1 && (
                  <button
                    type="button"
                    className="admin-dropdown-trigger"
                    style={{ padding: '0 10px' }}
                    onClick={() => addIngredient(index)}
                  >
                    +
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Directions */}
        <div>
          <label style={fieldLabelStyle}>Directions</label>
          {errors.directions && <span style={{ fontSize: '0.75rem', color: '#ec174f', display: 'block', marginBottom: 4 }}>{errors.directions}</span>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {directions.map((direction, index) => (
              <div key={index} style={{ display: 'flex', gap: 6 }}>
                <textarea
                  style={{ ...inputStyle, flex: 1, resize: 'vertical', minHeight: 60 }}
                  placeholder={`Step ${index + 1}`}
                  value={direction}
                  onChange={(e) => handleDirectionChange(index, e.target.value)}
                />
                {directions.length > 1 && (
                  <button
                    type="button"
                    className="admin-dropdown-trigger"
                    style={{ padding: '0 10px', alignSelf: 'flex-start' }}
                    onClick={() => removeDirection(index)}
                  >
                    −
                  </button>
                )}
                {index === directions.length - 1 && (
                  <button
                    type="button"
                    className="admin-dropdown-trigger"
                    style={{ padding: '0 10px', alignSelf: 'flex-start' }}
                    onClick={() => addDirection(index)}
                  >
                    +
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Image upload */}
        <div>
          <label style={fieldLabelStyle}>Recipe Image</label>
          <input
            ref={imageInputRef}
            type="file"
            name="recipeImage"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={onImageSelect}
          />
          <div
            onClick={() => imageInputRef.current?.click()}
            style={{
              border: '2px dashed #e1e3e5',
              borderRadius: 8,
              padding: 20,
              textAlign: 'center',
              cursor: 'pointer',
              background: '#fafafa',
            }}
          >
            {imageUrl ? (
              <>
                <img
                  src={imageUrl}
                  alt="recipe"
                  style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 6, marginBottom: 8 }}
                />
                <p style={{ fontSize: '0.78rem', color: '#6d7175', margin: 0 }}>Click to change</p>
              </>
            ) : (
              <>
                <i className="fas fa-image" style={{ fontSize: 28, color: '#c9cccf', marginBottom: 8 }} />
                <p style={{ fontSize: '0.82rem', color: '#6d7175', margin: 0 }}>Click to upload</p>
              </>
            )}
          </div>
          {errors.recipeImage && <span style={{ fontSize: '0.75rem', color: '#ec174f', display: 'block', marginTop: 4 }}>{errors.recipeImage}</span>}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            className="admin-dropdown-trigger"
            style={{ background: '#ec174f', color: '#fff', borderColor: '#ec174f' }}
            onClick={onSubmit}
            type="submit"
          >
            {loading ? 'Please wait…' : submitLabel}
          </button>
          <button
            type="button"
            className="admin-dropdown-trigger"
            onClick={onBack}
          >
            Back
          </button>
          {extraActions}
        </div>

      </div>
    </div>
  );
};

export default RecipeForm;
